// Optional bridge from the front door to the sound engine (src/audio/, owned by the sound agent).
// The engine is feature-detected through the build's asset manifest so a missing module never 404s, and
// every call is guarded, so the title screen works with no audio at all.
// Engine surface used (src/audio/index.js): initAudio, setVolume(bus, v), mute(bool), getSettings, play(name),
// createMusic(engine) -> { setEra(id), stop(fade) }.
// Settings persist in localStorage['driller.audio'] (same shape as src/audio/logic.js DEFAULT_SETTINGS), so the
// engine picks them up on its own init even when the bridge writes them first.
const KEY = 'driller.audio';
const DEFAULTS = { master: 0.8, music: 0.3, sfx: 0.8, ambience: 0.55, ui: 0.6, muted: false };
const BUSES = ['master', 'music', 'sfx', 'ambience', 'ui'];

let mod = null;
let detecting = null;
let music = null;
const waiters = [];

function read() {
  try {
    if (mod?.getSettings) return { ...DEFAULTS, ...mod.getSettings() };
    const o = JSON.parse(localStorage.getItem(KEY) || 'null') || {};
    const s = { ...DEFAULTS };
    for (const b of BUSES) if (Number.isFinite(+o[b])) s[b] = Math.min(1, Math.max(0, +o[b]));
    if (typeof o.muted === 'boolean') s.muted = o.muted;
    return s;
  } catch { return { ...DEFAULTS }; }
}

const safe = (fn) => { try { return fn(); } catch (e) { console.warn('[audio bridge]', e); return undefined; } };

async function detect() {
  try {
    const r = await fetch(new URL('../../asset-manifest.json', import.meta.url), { cache: 'no-cache' });
    if (!r.ok) return;
    const m = await r.json();
    // perf(load): production builds list bundled source modules under `modules` (scripts/gen-sw-manifest.js)
    if (![...(m.files ?? []), ...(m.modules ?? [])].some((f) => /(^|\/)src\/audio\/index\.js$/.test(f))) return;
    mod = await import('../audio/index.js');
    while (waiters.length) waiters.shift()();
  } catch (e) { console.info('[audio bridge] sound engine unavailable:', e?.message ?? e); }
}

export const audio = {
  detect() { return (detecting ??= detect()); },
  ready: () => !!mod,
  whenReady(cb) { if (mod) cb(); else waiters.push(cb); },
  settings: read,
  set(patch) {
    if (mod?.setVolume) {
      safe(() => {
        for (const [k, v] of Object.entries(patch)) {
          if (k === 'muted') mod.mute?.(!!v);
          else if (BUSES.includes(k)) mod.setVolume(k, v);
        }
      });
      return read();
    }
    const s = { ...read(), ...patch };
    try { localStorage.setItem(KEY, JSON.stringify(s)); } catch {}
    return s;
  },
  // Call from a user gesture (autoplay policy). Resolves when the AudioContext exists (or immediately if none).
  unlock() { return mod?.initAudio ? safe(() => mod.initAudio()) ?? Promise.resolve(null) : Promise.resolve(null); },
  play(name) { if (mod?.play && mod.isReady?.()) safe(() => mod.play(name)); },
  async titleMusic() {
    if (!mod?.createMusic || music) return;
    const e = await audio.unlock();
    if (!e || music) return;
    music = safe(() => mod.createMusic(e));
    safe(() => music?.setEra('pioneer', { fade: 3 }));
  },
  stopTitleMusic() { if (music) { const m = music; music = null; safe(() => m.stop(2.5)); } },
};
// perf(load): detection is started by title.js once the menu is up (was: on import), so the sound engine doesn't
// compete with the sim chunk on the title's critical path. audio.detect() is idempotent.
